import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  Divider,
  Alert,
  Snackbar,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Paper
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

const SystemConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Helper function to convert minutes to HH:MM format
  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Helper function to convert HH:MM to minutes
  const timeToMinutes = (timeStr) => {
    const [hours, mins] = timeStr.split(':').map(Number);
    return hours * 60 + mins;
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await apiGet('/api/system-config');
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      } else {
        throw new Error('Failed to fetch configuration');
      }
    } catch (error) {
      console.error('Error fetching configuration:', error);
      setSnackbar({ open: true, message: 'Failed to load configuration', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await apiPut('/api/system-config', config);

      if (response.ok) {
        setSnackbar({ open: true, message: 'Configuration saved successfully', severity: 'success' });
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = async () => {
    try {
      const response = await apiGet('/api/system-config/defaults');
      if (response.ok) {
        const defaults = await response.json();
        setConfig(defaults);
        setSnackbar({ open: true, message: 'Reset to default values', severity: 'info' });
      }
    } catch (error) {
      console.error('Error fetching defaults:', error);
      setSnackbar({ open: true, message: 'Failed to reset to defaults', severity: 'error' });
    }
  };

  const handleBusinessDayToggle = (day) => {
    const newDays = config.operational_hours.business_days.includes(day)
      ? config.operational_hours.business_days.filter(d => d !== day)
      : [...config.operational_hours.business_days, day].sort();
    
    setConfig({
      ...config,
      operational_hours: {
        ...config.operational_hours,
        business_days: newDays
      }
    });
  };

  const handleChange = (section, field, value) => {
    setConfig({
      ...config,
      [section]: {
        ...config[section],
        [field]: value
      }
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!config) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load configuration</Alert>
      </Box>
    );
  }

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <Box p={3}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <SettingsIcon sx={{ fontSize: 40, mr: 2, color: 'white' }} />
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
              System Configuration
            </Typography>
          </Box>
          <Box>
            <Button
              variant="outlined"
              startIcon={<RestoreIcon />}
              onClick={handleResetToDefaults}
              sx={{ mr: 2, color: 'white', borderColor: 'white' }}
            >
              Reset to Defaults
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ bgcolor: 'white', color: '#667eea', '&:hover': { bgcolor: '#f0f0f0' } }}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Resource Configuration */}
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#667eea', fontWeight: 'bold', mb: 2 }}>
                Resource Configuration
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TextField
                fullWidth
                label="Total Retreat Rooms"
                type="number"
                value={config.resource_config.total_retreat_rooms}
                onChange={(e) => handleChange('resource_config', 'total_retreat_rooms', parseInt(e.target.value))}
                margin="normal"
                helperText="Total number of retreat rooms available"
                InputProps={{ inputProps: { min: 0 } }}
              />
              
              <TextField
                fullWidth
                label="Total Retreat Occupancy"
                type="number"
                value={config.resource_config.total_retreat_occupancy}
                onChange={(e) => handleChange('resource_config', 'total_retreat_occupancy', parseInt(e.target.value))}
                margin="normal"
                helperText="Maximum retreat occupancy capacity"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Operational Hours */}
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#667eea', fontWeight: 'bold', mb: 2 }}>
                Operational Hours
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TextField
                fullWidth
                label="Operating Start Time"
                type="time"
                value={minutesToTime(config.operational_hours.start_time)}
                onChange={(e) => handleChange('operational_hours', 'start_time', timeToMinutes(e.target.value))}
                margin="normal"
                helperText="Daily operating start time"
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                fullWidth
                label="Operating End Time"
                type="time"
                value={minutesToTime(config.operational_hours.end_time)}
                onChange={(e) => handleChange('operational_hours', 'end_time', timeToMinutes(e.target.value))}
                margin="normal"
                helperText="Daily operating end time"
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                fullWidth
                label="Default Slot Duration (minutes)"
                type="number"
                value={config.operational_hours.default_slot_duration}
                onChange={(e) => handleChange('operational_hours', 'default_slot_duration', parseInt(e.target.value))}
                margin="normal"
                helperText="Standard booking increment"
                InputProps={{ inputProps: { min: 5, max: 120 } }}
              />
              
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Business Days
                </Typography>
                <FormGroup row>
                  {dayNames.map((day, index) => (
                    <FormControlLabel
                      key={index + 1}
                      control={
                        <Checkbox
                          checked={config.operational_hours.business_days.includes(index + 1)}
                          onChange={() => handleBusinessDayToggle(index + 1)}
                          color="primary"
                        />
                      }
                      label={day}
                    />
                  ))}
                </FormGroup>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Capacity & Load Management */}
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#667eea', fontWeight: 'bold', mb: 2 }}>
                Capacity & Load Management
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TextField
                fullWidth
                label="Maximum Concurrent Bookings"
                type="number"
                value={config.capacity_management.max_concurrent_bookings}
                onChange={(e) => handleChange('capacity_management', 'max_concurrent_bookings', parseInt(e.target.value))}
                margin="normal"
                helperText="System-wide capacity limit"
                InputProps={{ inputProps: { min: 0 } }}
              />
              
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2, mb: 1 }}>
                Peak Hours Definition
              </Typography>
              
              <TextField
                fullWidth
                label="Peak Hours Start Time"
                type="time"
                value={minutesToTime(config.capacity_management.peak_hours_start)}
                onChange={(e) => handleChange('capacity_management', 'peak_hours_start', timeToMinutes(e.target.value))}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                fullWidth
                label="Peak Hours End Time"
                type="time"
                value={minutesToTime(config.capacity_management.peak_hours_end)}
                onChange={(e) => handleChange('capacity_management', 'peak_hours_end', timeToMinutes(e.target.value))}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                fullWidth
                label="Therapist Utilization Target (%)"
                type="number"
                value={config.capacity_management.therapist_utilization_target}
                onChange={(e) => handleChange('capacity_management', 'therapist_utilization_target', parseInt(e.target.value))}
                margin="normal"
                helperText="Ideal percentage of therapist time booked"
                InputProps={{ inputProps: { min: 0, max: 100 } }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Alerting Thresholds */}
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#667eea', fontWeight: 'bold', mb: 2 }}>
                Alerting Thresholds
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TextField
                fullWidth
                label="Occupancy Alert Threshold (%)"
                type="number"
                value={config.alerting_thresholds.occupancy_alert_percentage}
                onChange={(e) => handleChange('alerting_thresholds', 'occupancy_alert_percentage', parseInt(e.target.value))}
                margin="normal"
                helperText="Alert when occupancy exceeds this percentage"
                InputProps={{ inputProps: { min: 0, max: 100 } }}
              />
              
              <TextField
                fullWidth
                label="Low Availability Threshold"
                type="number"
                value={config.alerting_thresholds.low_availability_threshold}
                onChange={(e) => handleChange('alerting_thresholds', 'low_availability_threshold', parseInt(e.target.value))}
                margin="normal"
                helperText="Alert when available slots drop below this number"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SystemConfig;


